/**
 * Gros plan : quand un intervenant prend la parole, il quitte le décor et
 * passe au premier plan quelques secondes, puis son texte rejoint le
 * procès-verbal. File d'attente si plusieurs interventions arrivent d'un coup.
 *
 * Jamais pour ses propres messages, jamais pour l'historique déjà présent au
 * chargement. La saisie de l'avocat reste au-dessus (z-index du parent).
 */
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CourtSprite, ROLE_COLORS, witnessVariant, type Expression, type StageRole } from './Courtroom';
import { plainExcerpt, stripSpeakerPrefix, type Side, type TrialMessage, type Witness } from './trialTypes';

const SPOT_ROLES = new Set(['judge', 'witness', 'prosecution', 'defense']);
const SKIP_TYPES = new Set(['cue', 'verdict']);
/** Au-delà, on ne garde que les plus récents : on ne rejoue pas une rafale. */
const MAX_QUEUE = 3;

/** Expression du personnage pendant sa mise en avant. */
export function expressionFor(m: TrialMessage): Expression {
  if (m.role === 'judge') return m.metadata?.incident ? 'angry' : 'talk';
  if (m.msg_type === 'incident') return m.role === 'defense' ? 'smug' : 'angry';
  return 'talk';
}

/** Durée d'affichage selon la longueur du texte (4 à 9 s). */
export function spotDuration(text: string) {
  return Math.min(9000, Math.max(4000, 2500 + Math.min(text.length, 420) * 16));
}

export function useSpotlightQueue({
  messages, myRole, enabled, ready,
}: {
  messages: TrialMessage[];
  myRole: Side | null;
  /** Salle en fond activée (le gros plan en fait partie). */
  enabled: boolean;
  /** L'affaire est chargée : ce qui est déjà là est de l'historique. */
  ready: boolean;
}) {
  const seenRef = useRef<Set<string> | null>(null);
  const [queue, setQueue] = useState<TrialMessage[]>([]);

  useEffect(() => {
    if (!ready) return;
    if (seenRef.current === null) {
      seenRef.current = new Set(messages.map(m => m.id));
      return;
    }
    const seen = seenRef.current;
    const fresh = messages.filter(m => !seen.has(m.id));
    if (!fresh.length) return;
    fresh.forEach(m => seen.add(m.id));
    if (!enabled) return;
    const eligible = fresh.filter(m =>
      SPOT_ROLES.has(m.role) && !SKIP_TYPES.has(m.msg_type) && m.role !== myRole && m.content.trim(),
    );
    if (eligible.length) setQueue(q => [...q, ...eligible].slice(-MAX_QUEUE));
  }, [messages, myRole, enabled, ready]);

  useEffect(() => { if (!enabled) setQueue([]); }, [enabled]);

  const dismiss = useCallback(() => setQueue(q => q.slice(1)), []);
  return { current: queue[0] ?? null, dismiss };
}

export function Spotlight({
  msg, messages, witnesses, onDone,
}: {
  msg: TrialMessage | null;
  messages: TrialMessage[];
  witnesses: Witness[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  const text = msg ? plainExcerpt(stripSpeakerPrefix(msg.content), 420) : '';
  const duration = spotDuration(text);

  useEffect(() => {
    if (!msg) return;
    const id = setTimeout(onDone, duration);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDone(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(id); window.removeEventListener('keydown', onKey); };
  }, [msg, duration, onDone]);

  let kicker = '';
  let context = '';
  if (msg) {
    const witnessName = (id?: string) => witnesses.find(w => w.id === id)?.name ?? '';
    if (msg.role === 'witness') {
      kicker = t('tribunal.court.spot.witness');
      const idx = messages.findIndex(m => m.id === msg.id);
      const q = messages.slice(0, idx).reverse().find(m =>
        (m.role === 'prosecution' || m.role === 'defense') && m.metadata?.witness_id === msg.metadata?.witness_id);
      if (q) context = t('tribunal.court.spot.answers', { name: q.speaker_name });
    } else if (msg.role === 'judge') {
      kicker = t('tribunal.court.spot.court');
      if (msg.metadata?.incident) context = t('tribunal.court.spot.rules_incident');
    } else {
      kicker = msg.msg_type === 'incident' ? t('tribunal.court.spot.incident') : t(`tribunal.seats.${msg.role}`);
      if (msg.metadata?.witness_id) context = t('tribunal.court.spot.questions', { name: witnessName(msg.metadata.witness_id) });
    }
  }

  const role = msg?.role ?? 'judge';
  const color = ROLE_COLORS[role] ?? ROLE_COLORS.system;
  const isBench = role === 'judge';
  const from = role === 'prosecution' ? { x: -320, y: 0, rotateY: 35 } : isBench ? { x: 0, y: -140, rotateY: 0 } : { x: 320, y: 0, rotateY: -35 };

  return (
    <AnimatePresence>
      {msg && (
        <motion.div
          key={msg.id}
          className="absolute inset-0 z-20 flex items-start justify-center overflow-hidden px-4 pt-3 sm:pt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.15 : 0.35 }}
        >
          <button
            type="button"
            aria-label={t('tribunal.court.spot.skip')}
            onClick={onDone}
            className="absolute inset-0 bg-stone-950/65 backdrop-blur-[6px] cursor-default"
          />
          <section
            aria-live="polite"
            className={`relative flex items-center gap-3 sm:gap-7 w-full max-w-3xl ${
              isBench ? 'flex-col' : role === 'prosecution' ? 'flex-col sm:flex-row' : 'flex-col sm:flex-row-reverse'
            }`}
            style={{ perspective: 1000 }}
          >
            <motion.div
              className="shrink-0"
              initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.45, ...from }}
              animate={{ opacity: 1, scale: 1, x: 0, y: 0, rotateY: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.7 }}
              transition={reduce ? { duration: 0.15 } : { type: 'spring', stiffness: 170, damping: 16, mass: 0.9 }}
            >
              <div
                // Sur téléphone, le portrait reste petit : la carte et son « Passer »
                // doivent tenir au-dessus du pupitre, qui ne se replie jamais.
                className={`relative rounded-full overflow-hidden w-24 h-24 ${isBench ? 'sm:w-[170px] sm:h-[170px]' : 'sm:w-[200px] sm:h-[200px]'}`}
                style={{ background: '#efe3c4', boxShadow: `0 0 0 5px ${color}, 0 24px 60px rgba(0,0,0,.5)` }}
              >
                <CourtSprite
                  role={role as StageRole}
                  expression={expressionFor(msg)}
                  variant={role === 'witness' ? witnessVariant(msg.metadata?.witness_id, witnesses) : ''}
                  talking
                  style={{ position: 'absolute', left: '-10%', top: '-6%', width: '120%', height: 'auto' }}
                />
              </div>
            </motion.div>

            <motion.div
              className="w-full max-w-xl rounded-2xl bg-card text-card-foreground p-4 sm:p-6 shadow-2xl"
              style={{ transformOrigin: '50% 100%' }}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.96, rotateX: 8 }}
              animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              transition={reduce ? { duration: 0.15 } : { type: 'spring', stiffness: 220, damping: 22, delay: 0.15 }}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md text-white" style={{ background: color }}>
                  {kicker}
                </span>
                {context && <span className="text-xs text-muted-foreground">{context}</span>}
              </div>
              <div className="mt-2 font-bold text-lg text-foreground">{msg.speaker_name || t(`tribunal.roles.${role}`)}</div>
              <motion.p
                className="mt-1.5 font-serif text-lg sm:text-xl leading-relaxed text-foreground line-clamp-5 sm:line-clamp-none"
                initial={reduce ? false : { clipPath: 'inset(0 0 100% 0)' }}
                animate={{ clipPath: 'inset(0 0 0% 0)' }}
                transition={{ duration: 1.3, ease: [0.4, 0, 0.2, 1], delay: 0.4 }}
              >
                {text}
              </motion.p>
              <div className="mt-4 flex items-center gap-3">
                <div aria-hidden="true" className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: color }}
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: duration / 1000, ease: 'linear' }}
                  />
                </div>
                <span className="hidden sm:inline text-xs text-muted-foreground">{t('tribunal.court.spot.to_record')}</span>
                <button
                  type="button"
                  onClick={onDone}
                  className="h-9 px-3.5 rounded-lg border border-border bg-background text-sm font-bold text-foreground hover:bg-muted"
                >
                  {t('tribunal.court.spot.skip')}
                </button>
              </div>
            </motion.div>
          </section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
